import * as pdfjsLib from 'pdfjs-dist';
import {
	matchBudgetsToCatalogue,
	parseCatalogueXlsx,
	type BudgetCatalogueResult,
	type CatalogueItem,
	type CatalogueMatch,
	type MatchStatus,
} from '../util/ndis-catalogue';
import {
	extractPlanModel,
	type NdisPlanModel,
	type PlanMetadata,
	type ReviewStatus,
} from '../util/ndis-plan-model';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString();

type ExtractionStatus = ReviewStatus;
type PdfExtractionStatus = 'idle' | 'extracting' | 'done' | 'error';

function injectStyles(): void {
	if (document.getElementById('ne-extraction-styles')) return;
	const style = document.createElement('style');
	style.id = 'ne-extraction-styles';
	style.textContent = ndisExtractionStyles();
	document.head.appendChild(style);
}

function shell(title: string): HTMLElement {
	document.title = title;
	injectStyles();

	// Override the dashboard layout so the testing page fills the viewport.
	document.body.classList.add('sp-public-body');
	const root = (document.getElementById('app') ?? document.body) as HTMLElement;
	root.classList.add('sp-public-root');
	root.innerHTML = '';

	// Hide any sidebar-scrim, modal-overlay, toast left from the dashboard shell.
	const hide = (id: string): void => {
		const el = document.getElementById(id);
		if (el) el.style.display = 'none';
	};
	hide('sidebar-scrim');
	hide('modal-overlay');
	hide('toast');

	const page = document.createElement('div');
	page.className = 'ne-page';
	root.appendChild(page);
	return page;
}

export function ndisExtractionStyles(): string {
	return `
    /* Body/root overrides match support public pages */
    body.sp-public-body {
      display: block !important;
      height: auto !important;
      min-height: 100dvh;
      background: #fffaf0 !important;
      overflow-y: auto !important;
    }
    .sp-public-root {
      display: block !important;
      width: 100% !important;
      height: auto !important;
      min-height: 0 !important;
      overflow: visible !important;
      flex: none !important;
    }

    .ne-page {
      min-height: 100dvh;
      width: 100%;
      background: #fffaf0;
      color: #18352a;
      font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
      padding: 24px 16px 48px;
      box-sizing: border-box;
    }
    .ne-shell { width: 100%; max-width: 760px; margin: 0 auto; }
    .ne-brand {
      color: #15803d;
      font-size: 15px;
      font-weight: 700;
      letter-spacing: 0.01em;
      margin-bottom: 20px;
    }
    .ne-hero { margin-bottom: 24px; }
    .ne-hero h1 {
      font-size: 28px;
      line-height: 1.2;
      margin: 0 0 10px;
      color: #18352a;
      font-weight: 800;
    }
    .ne-hero p {
      font-size: 16px;
      line-height: 1.55;
      margin: 0;
      color: #52665d;
    }
    .ne-notice {
      background: #fef3c7;
      border: 1px solid #f0ce76;
      border-radius: 12px;
      padding: 16px;
      color: #604414;
      font-size: 14px;
      line-height: 1.55;
      margin-bottom: 24px;
    }
    .ne-notice-warning {
      background: #fef3c7;
      border-left: 4px solid #d97706;
      color: #92400e;
    }
    .ne-section, .ne-card {
      background: #fff;
      border: 1px solid #d8e7d0;
      border-radius: 16px;
      box-shadow: 0 2px 12px rgba(29, 79, 50, 0.06);
    }
    .ne-upload-zone {
      min-height: 120px;
      border: 2px dashed #c9dcc1;
      border-radius: 12px;
      background: #fafdf8;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      cursor: pointer;
      padding: 20px;
      box-sizing: border-box;
      color: #3d5a4c;
      font-weight: 700;
      transition: border-color 0.15s, background 0.15s;
    }
    .ne-upload-zone:hover,
    .ne-upload-zone:focus-visible {
      border-color: #16a34a;
      outline: none;
    }
    .ne-upload-zone.ne-dragging {
      border-color: #16a34a;
      background: #f1f8ee;
    }
    .ne-upload-input {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      white-space: nowrap;
    }
    .ne-file-info {
      margin-top: 12px;
      color: #2d4a3a;
      font-size: 14px;
      font-weight: 700;
    }
    .ne-status {
      margin-top: 12px;
      color: #52665d;
      font-size: 14px;
      font-weight: 700;
    }
    .ne-status.ne-status-error { color: #b91c1c; }
    .ne-status.ne-status-done { color: #15803d; }
    .ne-cat-summary {
      padding: 12px 16px;
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      font-size: 14px;
      color: #166534;
    }
    .ne-privacy-note {
      margin: 12px 0 0;
      color: #7a9185;
      font-size: 13px;
      line-height: 1.55;
    }
    .ne-preview-toggle {
      margin-top: 16px;
      color: #15803d;
      background: transparent;
      border: none;
      padding: 0;
      font: inherit;
      font-weight: 800;
      cursor: pointer;
    }
    .ne-preview-wrap[hidden] { display: none; }
    .ne-raw-preview {
      max-height: 200px;
      overflow: hidden;
      background: #fafdf8;
      border: 1px solid #d8e7d0;
      border-radius: 10px;
      color: #18352a;
      font-family: ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', monospace;
      font-size: 13px;
      line-height: 1.5;
      margin: 10px 0 0;
      padding: 12px;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .ne-raw-preview.ne-expanded {
      max-height: none;
      overflow: visible;
    }
    .ne-plan-card {
      background: #fff;
      border: 1px solid #d8e7d0;
      border-radius: 16px;
      box-shadow: 0 2px 12px rgba(29, 79, 50, 0.06);
      padding: 18px;
      margin-bottom: 24px;
    }
    .ne-plan-card[hidden] { display: none; }
    .ne-section {
      padding: 20px;
      margin-bottom: 24px;
    }
    .ne-section h2 {
      font-size: 20px;
      margin: 0 0 8px;
      color: #18352a;
    }
    .ne-section-copy {
      margin: 0 0 16px;
      color: #52665d;
      font-size: 15px;
      line-height: 1.55;
    }
    .ne-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 14px;
      color: #3d5a4c;
      font-weight: 600;
    }
    .ne-field input, .ne-field textarea {
      width: 100%;
      min-height: 48px;
      border: 1.5px solid #c9dcc1;
      border-radius: 10px;
      background: #fafdf8;
      color: #18352a;
      font: inherit;
      font-weight: 500;
      font-size: 16px;
      padding: 12px 14px;
      box-sizing: border-box;
      outline: none;
      transition: border-color 0.15s, box-shadow 0.15s;
      -webkit-appearance: none;
    }
    .ne-field textarea { resize: vertical; min-height: 100px; }
    .ne-field textarea.ne-paste { min-height: 200px; }
    .ne-field input:focus, .ne-field textarea:focus {
      border-color: #16a34a;
      box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12);
    }
    .ne-field input[readonly], .ne-field textarea[readonly] {
      background: #f4faef;
      color: #52665d;
    }
    .ne-actions {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 18px;
    }
    .ne-button {
      min-height: 52px;
      border: none;
      border-radius: 12px;
      background: #16a34a;
      color: #fff;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      padding: 14px 20px;
      transition: background 0.15s, border-color 0.15s;
      -webkit-appearance: none;
      text-align: center;
    }
    .ne-button:active { background: #128a3e; }
    .ne-button-secondary {
      background: transparent;
      color: #15803d;
      border: 1.5px solid #c9dcc1;
    }
    .ne-button-secondary:active { background: #f1f8ee; }
    .ne-button-danger {
      background: transparent;
      color: #b91c1c;
      border: 1.5px solid #fecaca;
    }
    .ne-button-danger:active { background: #fef2f2; }
    .ne-results[hidden] { display: none; }
    .ne-summary {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
      background: #f1f8ee;
      border: 1px solid #c8dfc1;
      border-radius: 12px;
      padding: 14px;
      margin-bottom: 16px;
      color: #2d4a3a;
      font-size: 14px;
      font-weight: 700;
    }
    .ne-name-candidates { font-size: 0.85rem; color: #6b7280; margin-top: 0.25rem; }
    .ne-name-candidate { padding: 0.15rem 0; }
    .ne-name-candidate-source { font-style: italic; color: #9ca3af; }
    .ne-badge-unknown { background: #9ca3af; color: #fff; }
    .ne-badge-detected { background: #22c55e; color: #fff; }
    .ne-normalized-cat { font-size: 0.75rem; color: #9ca3af; font-family: monospace; }
    .ne-model-summary { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 0.5rem; padding: 0.75rem 1rem; margin-bottom: 1rem; }
    .ne-model-summary-row { display: flex; justify-content: space-between; gap: 1rem; font-size: 0.9rem; padding: 0.15rem 0; }
    .ne-model-summary-label { color: #6b7280; }
    .ne-model-summary-value { font-weight: 600; text-align: right; overflow-wrap: anywhere; }
    .ne-warning-list { list-style: none; padding: 0; margin: 0.5rem 0 0; }
    .ne-warning-item { font-size: 0.85rem; color: #b45309; padding: 0.15rem 0; }
    .ne-cards {
      display: grid;
      grid-template-columns: 1fr;
      gap: 16px;
    }
    .ne-card { padding: 18px; }
    .ne-card-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 16px;
    }
    .ne-card-title {
      margin: 0;
      color: #18352a;
      font-size: 18px;
      line-height: 1.3;
      font-weight: 800;
    }
    .ne-badge {
      border-radius: 999px;
      padding: 7px 10px;
      font-size: 12px;
      font-weight: 800;
      line-height: 1;
      white-space: nowrap;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
    .ne-badge-needs_review {
      background: #f59e0b;
      color: #fff;
    }
    .ne-badge-confirmed {
      color: #14532d;
      background: #dcfce7;
      border: 1px solid #86efac;
    }
    .ne-badge-rejected {
      color: #7f1d1d;
      background: #fee2e2;
      border: 1px solid #fecaca;
    }
    .ne-badge-high { background: #dcfce7; color: #166534; }
    .ne-badge-medium { background: #fef3c7; color: #92400e; }
    .ne-badge-low { background: #f3f4f6; color: #4b5563; }
    .ne-badge-selected { background: #dbeafe; color: #1e40af; }
    .ne-form-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 14px;
    }
    .ne-status-actions {
      display: grid;
      grid-template-columns: 1fr;
      gap: 10px;
      margin-top: 16px;
    }
    .ne-footer-note {
      color: #7a9185;
      font-size: 13px;
      line-height: 1.55;
      margin-top: 16px;
      text-align: center;
    }
    .ne-group-heading {
      font-size: 18px;
      font-weight: 800;
      color: #18352a;
      margin: 32px 0 12px;
      padding-bottom: 8px;
      border-bottom: 2px solid #d8e7d0;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .ne-group-heading .ne-group-count {
      font-size: 13px;
      font-weight: 700;
      color: #52665d;
      background: #f1f8ee;
      border-radius: 999px;
      padding: 3px 10px;
    }
    .ne-group-empty {
      color: #7a9185;
      font-size: 14px;
      line-height: 1.55;
      padding: 16px;
      background: #fafdf8;
      border: 1px dashed #d8e7d0;
      border-radius: 12px;
      margin-bottom: 16px;
    }
    .ne-budget-warning {
      background: #fef3c7;
      border: 1px solid #f0ce76;
      border-radius: 12px;
      padding: 14px 16px;
      color: #604414;
      font-size: 13px;
      line-height: 1.55;
      margin-bottom: 16px;
      font-weight: 600;
    }
    .ne-unknown-text {
      background: #fafdf8;
      border: 1px solid #d8e7d0;
      border-radius: 10px;
      padding: 12px;
      font-size: 14px;
      color: #18352a;
      line-height: 1.5;
      word-break: break-word;
      margin-bottom: 8px;
    }
    .ne-unknown-reason {
      color: #7a9185;
      font-size: 13px;
      margin-bottom: 12px;
    }
    .ne-match-heading {
      font-size: 18px;
      font-weight: 600;
      color: #18352a;
      margin: 24px 0 8px;
    }
    .ne-match-warning {
      font-size: 13px;
      color: #92400e;
      background: #fffbeb;
      padding: 8px 12px;
      border-radius: 6px;
      margin-bottom: 16px;
    }
    .ne-match-group-title {
      font-size: 15px;
      font-weight: 600;
      color: #18352a;
      margin: 16px 0 8px;
      padding: 8px 12px;
      background: #f8faf8;
      border-radius: 6px;
    }
    .ne-catalogue-review[hidden] { display: none; }
    .ne-cat-review-meta {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 10px;
      margin-bottom: 14px;
    }
    .ne-cat-review-stat {
      border: 1px solid var(--border);
      border-radius: 8px;
      background: var(--bg-secondary);
      padding: 10px;
      color: var(--fg);
      font-size: 13px;
    }
    .ne-cat-review-stat span {
      display: block;
      color: var(--fg-muted);
      font-size: 11px;
      margin-bottom: 4px;
      text-transform: uppercase;
    }
    .ne-cat-filter {
      width: 100%;
      min-height: 44px;
      border: 1px solid var(--border);
      border-radius: 8px;
      background: var(--bg);
      color: var(--fg);
      padding: 10px 12px;
      box-sizing: border-box;
      margin-bottom: 12px;
    }
    .ne-cat-table-wrap {
      max-height: 420px;
      overflow: auto;
      border: 1px solid var(--border);
      border-radius: 8px;
      background: var(--bg);
    }
    .ne-cat-table {
      width: 100%;
      min-width: 760px;
      border-collapse: collapse;
      font-size: 13px;
      color: var(--fg);
    }
    .ne-cat-table th, .ne-cat-table td {
      padding: 9px 10px;
      border-bottom: 1px solid var(--border);
      text-align: left;
      vertical-align: top;
    }
    .ne-cat-table th {
      position: sticky;
      top: 0;
      background: var(--bg-secondary);
      color: var(--fg-muted);
      font-size: 11px;
      text-transform: uppercase;
      z-index: 1;
    }
    .ne-cat-row {
      cursor: pointer;
    }
    .ne-cat-row-selected {
      outline: 2px solid var(--accent);
      outline-offset: -2px;
      background: var(--bg-secondary);
    }
    .ne-cat-detail td {
      background: var(--bg-secondary);
    }
    .ne-cat-detail-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
      gap: 8px;
      margin-bottom: 10px;
    }
    .ne-cat-detail-item {
      border: 1px solid var(--border);
      border-radius: 6px;
      padding: 8px;
      background: var(--bg);
    }
    .ne-cat-detail-item span {
      display: block;
      color: var(--fg-muted);
      font-size: 11px;
      margin-bottom: 3px;
    }
    .ne-cat-needs-review {
      display: inline-flex;
      padding: 3px 7px;
      border-radius: 999px;
      background: var(--warning);
      color: var(--bg);
      font-size: 11px;
      font-weight: 800;
      white-space: nowrap;
    }
    .ne-warning-stack {
      display: grid;
      gap: 8px;
      margin-top: 12px;
    }

    @media (min-width: 680px) {
      .ne-page { padding: 48px 24px 64px; }
      .ne-shell { max-width: 880px; }
      .ne-hero h1 { font-size: 36px; }
      .ne-section { padding: 24px; }
      .ne-form-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .ne-field-full { grid-column: 1 / -1; }
      .ne-actions, .ne-status-actions { flex-direction: row; }
      .ne-actions {
        display: flex;
        align-items: center;
      }
      .ne-status-actions { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .ne-button { width: auto; min-width: 150px; }
    }
  `;
}

function formatFileSize(size: number): string {
	if (size < 1024) return `${size} B`;
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
	return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function textContentItemToString(item: object): string {
	if ('str' in item && typeof item.str === 'string') return item.str;
	return '';
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.addEventListener('load', () => {
			if (reader.result instanceof ArrayBuffer) {
				resolve(reader.result);
				return;
			}
			reject(new Error('PDF file could not be read.'));
		});
		reader.addEventListener('error', () => {
			reject(new Error('PDF file could not be read.'));
		});
		reader.readAsArrayBuffer(file);
	});
}

async function extractTextFromPdf(file: File): Promise<string> {
	const data = await readFileAsArrayBuffer(file);
	const pdf = await pdfjsLib.getDocument({ data }).promise;
	const pageTexts: string[] = [];

	for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
		const page = await pdf.getPage(pageNumber);
		const content = await page.getTextContent();
		pageTexts.push(content.items.map(textContentItemToString).filter(Boolean).join(' '));
	}

	return pageTexts.join('\n\n').trim();
}

function statusLabel(status: ExtractionStatus): string {
	if (status === 'confirmed') return 'confirmed';
	if (status === 'rejected') return 'rejected';
	return 'needs_review';
}

function amountValue(amount: string): number | null {
	const numeric = Number(amount.replace(/[$,\s]/g, ''));
	return Number.isFinite(numeric) ? numeric : null;
}

function renderModelSummary(summary: HTMLElement, model: NdisPlanModel): void {
	const allItems = [
		...model.chargeCodes.map((i) => i.status),
		...model.budgets.map((i) => i.status),
		...model.unknownLines.map((i) => i.status),
	];
	const confirmed = allItems.filter((s) => s === 'confirmed').length;
	const rejected = allItems.filter((s) => s === 'rejected').length;
	const needsReview = allItems.filter((s) => s === 'needs_review').length;
	const amounts = model.budgets.map((budget) => amountValue(budget.amount));
	const totalAmount = amounts.every((value) => value !== null)
		? amounts.reduce((sum, value) => sum + (value ?? 0), 0)
		: null;
	const totalAmountText = totalAmount === null
		? 'Not calculable'
		: new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(totalAmount);

	summary.innerHTML = `
    <h3 class="ne-section-title">Plan model summary</h3>
    <div class="ne-model-summary-row"><span class="ne-model-summary-label">Extracted</span><span class="ne-model-summary-value">${model.extractionTimestamp}</span></div>
    <div class="ne-model-summary-row"><span class="ne-model-summary-label">Participant name status</span><span class="ne-model-summary-value">${model.metadata.participantNameStatus}</span></div>
    <div class="ne-model-summary-row"><span class="ne-model-summary-label">Budgets</span><span class="ne-model-summary-value">${model.budgets.length} (${totalAmountText})</span></div>
    <div class="ne-model-summary-row"><span class="ne-model-summary-label">Charge codes</span><span class="ne-model-summary-value">${model.chargeCodes.length}</span></div>
    <div class="ne-model-summary-row"><span class="ne-model-summary-label">Warnings</span><span class="ne-model-summary-value">${model.metadata.warnings.length}</span></div>
    <div class="ne-model-summary-row"><span class="ne-model-summary-label">Review status</span><span class="ne-model-summary-value">${confirmed} confirmed / ${rejected} rejected / ${needsReview} needs review</span></div>
  `;
}

function renderPlanCard(card: HTMLElement, plan: PlanMetadata): void {
	card.hidden = false;
	card.innerHTML = '';

	const head = document.createElement('div');
	head.className = 'ne-card-head';
	const title = document.createElement('h2');
	title.className = 'ne-card-title';
	title.textContent = 'Plan details';
	const badge = document.createElement('span');
	badge.className = `ne-badge ne-badge-${plan.participantNameStatus}`;
	badge.textContent = plan.participantNameStatus === 'needs_review' ? 'Needs review' : plan.participantNameStatus;
	head.append(title, badge);

	const grid = document.createElement('div');
	grid.className = 'ne-form-grid';
	grid.append(
		createField('Participant name', createInput(plan.participantName || 'Not detected', (value) => { plan.participantName = value; })),
		createField('NDIS number', createInput(plan.ndisNumber || 'Not detected', (value) => { plan.ndisNumber = value; })),
		createField('Plan start date', createInput(plan.planStartDate || 'Not detected', (value) => { plan.planStartDate = value; })),
		createField('Plan end date', createInput(plan.planEndDate || 'Not detected', (value) => { plan.planEndDate = value; })),
		createField('Management type', createInput(plan.managementType || 'Not detected', (value) => { plan.managementType = value; }), true),
	);

	card.append(head, grid);

	if (plan.participantNameCandidates.length > 0) {
		const candidates = document.createElement('div');
		candidates.className = 'ne-name-candidates';
		const label = document.createElement('div');
		label.textContent = 'Possible names found:';
		candidates.appendChild(label);
		for (const candidate of plan.participantNameCandidates) {
			const item = document.createElement('div');
			item.className = 'ne-name-candidate';
			const value = document.createElement('span');
			value.textContent = candidate.value;
			const source = document.createElement('span');
			source.className = 'ne-name-candidate-source';
			source.textContent = ` ${candidate.source}`;
			item.append(value, source);
			candidates.appendChild(item);
		}
		card.appendChild(candidates);
	}

	if (plan.warnings.length > 0) {
		const warnings = document.createElement('ul');
		warnings.className = 'ne-warning-list';
		for (const warning of plan.warnings) {
			const item = document.createElement('li');
			item.className = 'ne-warning-item';
			item.textContent = warning;
			warnings.appendChild(item);
		}
		card.appendChild(warnings);
	}
}

function bindEditableField(input: HTMLInputElement | HTMLTextAreaElement, onChange: (value: string) => void): void {
	input.addEventListener('input', () => {
		onChange(input.value);
	});
}

function createButton(className: string, text: string, onClick: () => void): HTMLButtonElement {
	const button = document.createElement('button');
	button.className = className;
	button.type = 'button';
	button.textContent = text;
	button.addEventListener('click', onClick);
	return button;
}

function createInput(value: string, onChange: (value: string) => void): HTMLInputElement {
	const input = document.createElement('input');
	input.type = 'text';
	input.value = value;
	bindEditableField(input, onChange);
	return input;
}

function createTextarea(value: string, onChange: (value: string) => void): HTMLTextAreaElement {
	const textarea = document.createElement('textarea');
	textarea.rows = 4;
	textarea.value = value;
	bindEditableField(textarea, onChange);
	return textarea;
}

function createField(labelText: string, control: HTMLInputElement | HTMLTextAreaElement, full = false): HTMLLabelElement {
	const label = document.createElement('label');
	label.className = full ? 'ne-field ne-field-full' : 'ne-field';
	const span = document.createElement('span');
	span.textContent = labelText;
	label.append(span, control);
	return label;
}

function renderStatusActions(
	item: { status: ExtractionStatus },
	redraw: () => void,
): HTMLDivElement {
	const statusActions = document.createElement('div');
	statusActions.className = 'ne-status-actions';
	statusActions.append(
		createButton('ne-button', 'Confirm', () => {
			item.status = 'confirmed';
			redraw();
		}),
		createButton('ne-button ne-button-danger', 'Reject', () => {
			item.status = 'rejected';
			redraw();
		}),
		createButton('ne-button ne-button-secondary', 'Reset', () => {
			item.status = 'needs_review';
			redraw();
		}),
	);
	return statusActions;
}

function confidenceBadgeClass(match: CatalogueMatch): string {
	if (match.confidence === 'high') return 'ne-badge ne-badge-high';
	if (match.confidence === 'medium') return 'ne-badge ne-badge-medium';
	return 'ne-badge ne-badge-low';
}

function renderCatalogueStatusActions(
	match: { status: MatchStatus },
	redraw: () => void,
): HTMLDivElement {
	const statusActions = document.createElement('div');
	statusActions.className = 'ne-status-actions';
	statusActions.append(
		createButton('ne-button', 'Select', () => {
			match.status = 'selected';
			redraw();
		}),
		createButton('ne-button ne-button-danger', 'Reject', () => {
			match.status = 'rejected';
			redraw();
		}),
		createButton('ne-button ne-button-secondary', 'Reset', () => {
			match.status = 'possible';
			redraw();
		}),
	);
	return statusActions;
}

function renderCatalogueMatchCard(match: CatalogueMatch, redraw: () => void): HTMLElement {
	const card = document.createElement('article');
	card.className = 'ne-card';

	const head = document.createElement('div');
	head.className = 'ne-card-head';
	const title = document.createElement('h3');
	title.className = 'ne-card-title';
	title.textContent = `${match.catalogueItem.supportItemNumber} ${match.catalogueItem.supportItemName}`;
	const badge = document.createElement('span');
	badge.className = confidenceBadgeClass(match);
	badge.textContent = match.confidence;
	head.append(title, badge);

	const grid = document.createElement('div');
	grid.className = 'ne-form-grid';
	grid.append(
		createField('Category', createInput(match.catalogueItem.supportCategory, (value) => { match.catalogueItem.supportCategory = value; })),
		createField('Unit', createInput(match.catalogueItem.unit, (value) => { match.catalogueItem.unit = value; })),
		createField('Price limit', createInput(match.catalogueItem.priceLimit, (value) => { match.catalogueItem.priceLimit = value; })),
		createField('Match reason', createInput(match.matchReason, (value) => { match.matchReason = value; }), true),
		createField('Notes', createTextarea(match.notes, (value) => { match.notes = value; }), true),
	);

	card.append(head, grid, renderCatalogueStatusActions(match, redraw));
	return card;
}

function renderSelectedCatalogueItems(container: HTMLElement, matches: BudgetCatalogueResult[]): void {
	const selectedMatches = matches.flatMap((result) => result.matches.filter((match) => match.status === 'selected'));
	if (selectedMatches.length === 0) return;

	const heading = document.createElement('h3');
	heading.className = 'ne-section-title ne-match-heading';
	heading.textContent = 'Selected items';
	container.appendChild(heading);

	const warning = document.createElement('p');
	warning.className = 'ne-match-warning';
	warning.textContent = 'Selected items are not invoice-ready until final confirmation.';
	container.appendChild(warning);

	for (const match of selectedMatches) {
		const card = document.createElement('article');
		card.className = 'ne-card';
		const head = document.createElement('div');
		head.className = 'ne-card-head';
		const title = document.createElement('h3');
		title.className = 'ne-card-title';
		title.textContent = `${match.catalogueItem.supportItemNumber} ${match.catalogueItem.supportItemName}`;
		const badge = document.createElement('span');
		badge.className = 'ne-badge ne-badge-selected';
		badge.textContent = 'Selected';
		head.append(title, badge);

		const grid = document.createElement('div');
		grid.className = 'ne-form-grid';
		grid.append(
			createField('Category', createInput(match.catalogueItem.supportCategory, (value) => { match.catalogueItem.supportCategory = value; })),
			createField('Unit', createInput(match.catalogueItem.unit, (value) => { match.catalogueItem.unit = value; })),
			createField('Price', createInput(match.catalogueItem.priceLimit, (value) => { match.catalogueItem.priceLimit = value; })),
			createField('Notes', createTextarea(match.notes, (value) => { match.notes = value; }), true),
		);
		card.append(head, grid);
		container.appendChild(card);
	}
}

function renderCatalogueMatches(
	section: HTMLElement,
	cards: HTMLElement,
	model: NdisPlanModel | null,
	matches: BudgetCatalogueResult[],
	catalogueLoaded: boolean,
): void {
	cards.innerHTML = '';

	if (!model || model.budgets.length === 0) {
		section.hidden = true;
		return;
	}

	section.hidden = false;
	const heading = document.createElement('h3');
	heading.className = 'ne-section-title ne-match-heading';
	heading.textContent = 'Possible catalogue matches for plan budgets';
	cards.appendChild(heading);

	const warning = document.createElement('p');
	warning.className = 'ne-match-warning';
	warning.textContent = 'Plan budgets show available funding categories. Support item codes are claimable line items. Matches are suggestions only — review before use.';
	cards.appendChild(warning);

	const warningStack = document.createElement('div');
	warningStack.className = 'ne-warning-stack';
	for (const message of [
		'Do not treat plan budget categories as invoice line codes',
		'Catalogue version and rates must be confirmed against current NDIA pricing',
		'Official source: NDIS Support Catalogue',
	]) {
		const item = document.createElement('div');
		item.className = 'ne-match-warning';
		item.textContent = message;
		warningStack.appendChild(item);
	}
	cards.appendChild(warningStack);

	if (!catalogueLoaded) {
		const empty = document.createElement('div');
		empty.className = 'ne-group-empty';
		empty.textContent = 'No chargeable support item codes were found in the plan text. Load the official Support Catalogue to search possible line items.';
		cards.appendChild(empty);
		return;
	}

	const matchedResults = matches.filter((result) => result.matches.length > 0);
	if (matchedResults.length === 0) {
		const empty = document.createElement('div');
		empty.className = 'ne-group-empty';
		empty.textContent = 'No catalogue matches found for the extracted budget categories. The catalogue may use different category names, or the budget categories may not have direct support item equivalents.';
		cards.appendChild(empty);
		return;
	}

	const redraw = (): void => {
		renderCatalogueMatches(section, cards, model, matches, catalogueLoaded);
	};

	for (const result of matchedResults) {
		const amountSuffix = result.budgetAmount ? ` (${result.budgetAmount})` : '';
		const groupTitle = document.createElement('div');
		groupTitle.className = 'ne-match-group-title';
		groupTitle.textContent = `Matches for: ${result.budgetCategory}${amountSuffix}`;
		cards.appendChild(groupTitle);

		for (const match of result.matches) {
			cards.appendChild(renderCatalogueMatchCard(match, redraw));
		}
	}

	renderSelectedCatalogueItems(cards, matches);
}

function catalogueNeedsReview(item: CatalogueItem): boolean {
	return !item.supportItemNumber || !item.supportItemName || !item.supportCategory || !item.unit || !item.priceLimit || !item.registrationGroup;
}

function detectCatalogueMetadata(fileName: string): { version: string; date: string } {
	const dateMatch = fileName.match(/(20\d{2})[-_ ]?([01]\d)[-_ ]?([0-3]\d)/);
	const yearMatch = fileName.match(/(20\d{2})/);
	return {
		version: yearMatch?.[1] ? `Detected ${yearMatch[1]}` : 'Not detected',
		date: dateMatch?.[1] && dateMatch[2] && dateMatch[3] ? `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}` : 'Not detected',
	};
}

function catalogueSearchText(item: CatalogueItem): string {
	return [
		item.supportItemNumber,
		item.supportItemName,
		item.supportCategory,
		item.registrationGroup,
		item.unit,
		item.priceLimit,
	].join(' ').toLowerCase();
}

function renderCatalogueReviewPanel(
	panel: HTMLElement,
	items: CatalogueItem[],
	fileName: string,
	selectedIds: Set<string>,
	filter = '',
	expandedId = '',
): void {
	if (items.length === 0) {
		panel.hidden = true;
		panel.innerHTML = '';
		return;
	}

	const metadata = detectCatalogueMetadata(fileName);
	const query = filter.trim().toLowerCase();
	const visibleItems = query ? items.filter((item) => catalogueSearchText(item).includes(query)) : items;
	panel.hidden = false;
	panel.innerHTML = '';

	const title = document.createElement('h2');
	title.textContent = 'Catalogue Review';
	const copy = document.createElement('p');
	copy.className = 'ne-section-copy';
	copy.textContent = 'Review loaded Support Catalogue items before selecting any line items for an invoice draft.';

	const meta = document.createElement('div');
	meta.className = 'ne-cat-review-meta';
	for (const stat of [
		{ label: 'Version', value: metadata.version },
		{ label: 'Date', value: metadata.date },
		{ label: 'Items loaded', value: String(items.length) },
		{ label: 'Selected', value: String(selectedIds.size) },
	]) {
		const box = document.createElement('div');
		box.className = 'ne-cat-review-stat';
		const label = document.createElement('span');
		label.textContent = stat.label;
		const value = document.createElement('strong');
		value.textContent = stat.value;
		box.append(label, value);
		meta.appendChild(box);
	}

	const search = document.createElement('input');
	search.className = 'ne-cat-filter';
	search.type = 'search';
	search.value = filter;
	search.placeholder = 'Filter by item number, name, category, or registration group';
	search.addEventListener('input', () => {
		renderCatalogueReviewPanel(panel, items, fileName, selectedIds, search.value, expandedId);
	});

	const wrap = document.createElement('div');
	wrap.className = 'ne-cat-table-wrap';
	const table = document.createElement('table');
	table.className = 'ne-cat-table';
	const thead = document.createElement('thead');
	thead.innerHTML = '<tr><th>Item number</th><th>Name</th><th>Category</th><th>Unit</th><th>Price limit</th><th>Registration group</th><th>Review</th></tr>';
	const tbody = document.createElement('tbody');

	for (const item of visibleItems) {
		const id = item.supportItemNumber;
		const row = document.createElement('tr');
		row.className = selectedIds.has(id) ? 'ne-cat-row ne-cat-row-selected' : 'ne-cat-row';
		row.tabIndex = 0;
		const review = catalogueNeedsReview(item) ? '<span class="ne-cat-needs-review">needs review</span>' : '';
		row.innerHTML = `<td></td><td></td><td></td><td></td><td></td><td></td><td>${review}</td>`;
		const cells = row.querySelectorAll<HTMLTableCellElement>('td');
		const values = [item.supportItemNumber, item.supportItemName, item.supportCategory, item.unit, item.priceLimit, item.registrationGroup];
		values.forEach((value, index) => {
			const cell = cells[index];
			if (cell) cell.textContent = value || 'Not detected';
		});
		const expand = (): void => {
			renderCatalogueReviewPanel(panel, items, fileName, selectedIds, filter, expandedId === id ? '' : id);
		};
		row.addEventListener('click', expand);
		row.addEventListener('keydown', (event) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				expand();
			}
		});
		tbody.appendChild(row);

		if (expandedId === id) {
			const detailRow = document.createElement('tr');
			detailRow.className = 'ne-cat-detail';
			const cell = document.createElement('td');
			cell.colSpan = 7;
			const grid = document.createElement('div');
			grid.className = 'ne-cat-detail-grid';
			const fields: Array<{ label: string; value: string }> = [
				{ label: 'Item number', value: item.supportItemNumber },
				{ label: 'Name', value: item.supportItemName },
				{ label: 'Category', value: item.supportCategory },
				{ label: 'Unit', value: item.unit },
				{ label: 'Price limit', value: item.priceLimit },
				{ label: 'Registration group', value: item.registrationGroup },
				{ label: 'Quote required', value: item.quoteRequired ? 'Yes' : 'No' },
				{ label: 'Non-face-to-face', value: item.nonFaceToFace ? 'Yes' : 'No' },
				{ label: 'Travel', value: item.providerTravel ? 'Yes' : 'No' },
				{ label: 'Short notice cancellation', value: item.shortNoticeCancellation ? 'Yes' : 'No' },
				{ label: 'Remote price', value: item.remotePriceLimit },
				{ label: 'Very remote price', value: item.veryRemotePriceLimit },
			];
			for (const field of fields) {
				const box = document.createElement('div');
				box.className = 'ne-cat-detail-item';
				const label = document.createElement('span');
				label.textContent = field.label;
				const value = document.createElement('strong');
				value.textContent = field.value || 'Not detected';
				box.append(label, value);
				grid.appendChild(box);
			}
			const toggle = createButton('ne-button ne-button-secondary', selectedIds.has(id) ? 'Remove from invoice draft selection' : 'Select for invoice draft', () => {
				if (selectedIds.has(id)) selectedIds.delete(id);
				else selectedIds.add(id);
				renderCatalogueReviewPanel(panel, items, fileName, selectedIds, filter, expandedId);
			});
			cell.append(grid, toggle);
			detailRow.appendChild(cell);
			tbody.appendChild(detailRow);
		}
	}

	table.append(thead, tbody);
	wrap.appendChild(table);

	const warnings = document.createElement('div');
	warnings.className = 'ne-warning-stack';
	for (const message of [
		'Do not treat plan budget categories as invoice line codes',
		'Catalogue version and rates must be confirmed against current NDIA pricing',
		'Official source: NDIS Support Catalogue',
	]) {
		const item = document.createElement('div');
		item.className = 'ne-match-warning';
		item.textContent = message;
		warnings.appendChild(item);
	}

	panel.append(title, copy, meta, search, wrap, warnings);
}

function renderGroupHeading(label: string, count: number): HTMLHeadingElement {
	const h = document.createElement('h3');
	h.className = 'ne-group-heading';
	h.innerHTML = `${label} <span class="ne-group-count">${count}</span>`;
	return h;
}

function renderAllSections(container: HTMLElement, summary: HTMLElement, model: NdisPlanModel): void {
	container.innerHTML = '';
	const redraw = (): void => {
		renderAllSections(container, summary, model);
		renderModelSummary(summary, model);
	};

	// ── Budgets / funding amounts ──
	container.appendChild(renderGroupHeading('Budgets / funding amounts', model.budgets.length));
	const budgetWarn = document.createElement('div');
	budgetWarn.className = 'ne-budget-warning';
	budgetWarn.textContent = 'Budgets show available plan funding. Support item codes are the items that may be used for charging. Catalogue matches are suggestions only and must be reviewed before use.';
	container.appendChild(budgetWarn);

	if (model.budgets.length === 0) {
		const empty = document.createElement('div');
		empty.className = 'ne-group-empty';
		empty.textContent = 'No budget/funding lines detected. The plan may list budgets in a format not yet recognised.';
		container.appendChild(empty);
	} else {
		for (const item of model.budgets) {
			const card = document.createElement('article');
			card.className = 'ne-card';
			const head = document.createElement('div');
			head.className = 'ne-card-head';
			const titleWrap = document.createElement('div');
			const title = document.createElement('h3');
			title.className = 'ne-card-title';
			title.textContent = item.categoryName;
			const normalized = document.createElement('div');
			normalized.className = 'ne-normalized-cat';
			normalized.textContent = item.normalizedCategory;
			titleWrap.append(title, normalized);
			const badge = document.createElement('span');
			badge.className = `ne-badge ne-badge-${item.status}`;
			badge.textContent = statusLabel(item.status);
			head.append(titleWrap, badge);

			const grid = document.createElement('div');
			grid.className = 'ne-form-grid';
			const source = document.createElement('textarea');
			source.rows = 2;
			source.value = item.sourceSnippet;
			source.readOnly = true;
			grid.append(
				createField('Category', createInput(item.categoryName, (v) => { item.categoryName = v; })),
				createField('Amount', createInput(item.amount, (v) => { item.amount = v; })),
				createField('Management type', createInput(item.managementType, (v) => { item.managementType = v; })),
				createField('Section title', createInput(item.sourceSectionTitle, (v) => { item.sourceSectionTitle = v; })),
				createField('Notes', createInput(item.notes, (v) => { item.notes = v; })),
				createField('Source snippet', source, true),
			);
			card.append(head, grid);
			if (item.warnings.length > 0) {
				const warnings = document.createElement('ul');
				warnings.className = 'ne-warning-list';
				for (const warning of item.warnings) {
					const warningItem = document.createElement('li');
					warningItem.className = 'ne-warning-item';
					warningItem.textContent = warning;
					warnings.appendChild(warningItem);
				}
				card.appendChild(warnings);
			}
			card.appendChild(renderStatusActions(item, redraw));
			container.appendChild(card);
		}
	}

	// ── Support item codes for charging ──
	container.appendChild(renderGroupHeading('Support item codes for charging', model.chargeCodes.length));

	if (model.chargeCodes.length === 0) {
		const empty = document.createElement('div');
		empty.className = 'ne-group-empty';
		empty.textContent = 'No chargeable support item codes were found in the plan text. NDIS plans commonly show budgets and funding categories, while support item codes usually come from the official Support Catalogue, service agreements, provider schedules, or invoicing rules.';
		container.appendChild(empty);
	} else {
		model.chargeCodes.forEach((item, index) => {
			const card = document.createElement('article');
			card.className = 'ne-card';
			const head = document.createElement('div');
			head.className = 'ne-card-head';
			const title = document.createElement('h3');
			title.className = 'ne-card-title';
			title.textContent = `Charge code ${index + 1}`;
			const badge = document.createElement('span');
			badge.className = `ne-badge ne-badge-${item.status}`;
			badge.textContent = statusLabel(item.status);
			head.append(title, badge);

			const grid = document.createElement('div');
			grid.className = 'ne-form-grid';
			const source = document.createElement('textarea');
			source.rows = 2;
			source.value = item.sourceSnippet;
			source.readOnly = true;
			grid.append(
				createField('Support item code', createInput(item.code, (v) => { item.code = v; })),
				createField('Description/name', createInput(item.description, (v) => { item.description = v; })),
				createField('Category', createInput(item.category, (v) => { item.category = v; })),
				createField('Rate/unit', createInput(item.rate, (v) => { item.rate = v; })),
				createField('Source snippet', source, true),
				createField('Notes', createTextarea(item.notes, (v) => { item.notes = v; }), true),
			);
			card.append(head, grid, renderStatusActions(item, redraw));
			container.appendChild(card);
		});
	}

	// ── Needs review ──
	if (model.unknownLines.length > 0) {
		container.appendChild(renderGroupHeading('Needs review', model.unknownLines.length));
		for (const item of model.unknownLines) {
			const card = document.createElement('article');
			card.className = 'ne-card';
			const head = document.createElement('div');
			head.className = 'ne-card-head';
			const title = document.createElement('h3');
			title.className = 'ne-card-title';
			title.textContent = 'Unclassified';
			const badge = document.createElement('span');
			badge.className = `ne-badge ne-badge-${item.status}`;
			badge.textContent = statusLabel(item.status);
			head.append(title, badge);

			const textEl = document.createElement('div');
			textEl.className = 'ne-unknown-text';
			textEl.textContent = item.text;
			const reasonEl = document.createElement('div');
			reasonEl.className = 'ne-unknown-reason';
			reasonEl.textContent = item.reason;

			const grid = document.createElement('div');
			grid.className = 'ne-form-grid';
			grid.append(
				createField('Notes', createTextarea(item.notes, (v) => { item.notes = v; }), true),
			);

			card.append(head, textEl, reasonEl, grid, renderStatusActions(item, redraw));
			container.appendChild(card);
		}
	}
}

export function renderNdisExtraction(): void {
	const page = shell('NDIS plan code extraction - Hub People');
	page.innerHTML = `
    <main class="ne-shell">
      <div class="ne-brand">the hub ecosystems</div>
      <header class="ne-hero">
        <h1>NDIS plan code extraction</h1>
        <p>Paste draft NDIS plan text or line items to generate editable support code suggestions for review.</p>
      </header>
      <div class="ne-notice">This is testing only. Do not use extracted or matched codes for invoicing until reviewed and confirmed.</div>
      <section class="ne-section" aria-labelledby="ne-pdf-title">
        <h2 id="ne-pdf-title">Upload PDF plan</h2>
        <p class="ne-section-copy">Extract all readable text from a PDF plan in this browser, then generate editable draft fields for review.</p>
        <input id="ne-pdf-file" class="ne-upload-input" type="file" accept="application/pdf,.pdf">
        <div id="ne-upload-zone" class="ne-upload-zone" role="button" tabindex="0" aria-controls="ne-pdf-file">
          <span>Choose a PDF or drop it here</span>
        </div>
        <div id="ne-file-info" class="ne-file-info" hidden></div>
        <div id="ne-pdf-status" class="ne-status">Status: idle</div>
        <p class="ne-privacy-note">Test only — uploaded file is not saved or transmitted.</p>
        <p id="ne-pdf-fallback" class="ne-privacy-note" hidden>If PDF text cannot be read, copy and paste the plan text below.</p>
        <div class="ne-actions">
          <button id="ne-pdf-extract" class="ne-button" type="button" disabled>Extract from PDF</button>
        </div>
        <div id="ne-preview-wrap" class="ne-preview-wrap" hidden>
          <button id="ne-preview-toggle" class="ne-preview-toggle" type="button" aria-expanded="false">Show extracted raw text</button>
          <pre id="ne-raw-preview" class="ne-raw-preview"></pre>
        </div>
      </section>
      <section id="ne-plan-card" class="ne-plan-card" hidden aria-live="polite"></section>
      <section class="ne-section" aria-labelledby="ne-paste-title">
        <h2 id="ne-paste-title">Paste plan text</h2>
        <p class="ne-section-copy">This mock tool looks for NDIS-like support item codes and dollar amounts in pasted text.</p>
        <label class="ne-field">
          <span>NDIS plan text or line items</span>
          <textarea id="ne-input" class="ne-paste" rows="10" autocomplete="off" spellcheck="false"></textarea>
        </label>
        <div class="ne-actions">
          <button id="ne-extract" class="ne-button" type="button">Extract codes</button>
        </div>
      </section>
      <section class="ne-section" aria-labelledby="ne-cat-title">
        <h2 id="ne-cat-title">NDIS Support Catalogue reference</h2>
        <p class="ne-section-copy">Upload the official NDIS Support Catalogue XLSX to find possible support item codes for your plan budget categories.</p>
        <div class="ne-notice ne-notice-warning">Budgets show available plan funding. Support item codes are the items that may be used for charging. Catalogue matches are suggestions only and must be reviewed before use.</div>
        <input id="ne-cat-file" class="ne-upload-input" type="file" accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel">
        <div id="ne-cat-zone" class="ne-upload-zone" role="button" tabindex="0" aria-controls="ne-cat-file">
          <span>Choose XLSX catalogue or drop it here</span>
        </div>
        <div id="ne-cat-info" class="ne-file-info" hidden></div>
        <div id="ne-cat-status" class="ne-status">Status: idle</div>
        <p class="ne-privacy-note">Test only - file is processed in this browser only, not saved or transmitted.</p>
        <p class="ne-privacy-note">Official NDIS documents can change. Always confirm the current Support Catalogue and Pricing Arrangements before relying on rates.</p>
        <div class="ne-actions">
          <button id="ne-cat-parse" class="ne-button" type="button" disabled>Load catalogue</button>
        </div>
        <div id="ne-cat-summary" class="ne-cat-summary" hidden></div>
      </section>
      <section id="ne-cat-review" class="ne-section ne-catalogue-review" hidden aria-live="polite"></section>
      <section id="ne-results" class="ne-results" hidden aria-live="polite">
        <div id="ne-summary" class="ne-model-summary"></div>
        <div id="ne-cards" class="ne-cards"></div>
        <div id="ne-match-section" hidden>
          <div id="ne-match-cards" class="ne-cards"></div>
        </div>
      </section>
      <p class="ne-footer-note">Draft extraction only. Not invoice-ready until reviewed.</p>
    </main>
  `;

	const input = document.getElementById('ne-input') as HTMLTextAreaElement | null;
	const extract = document.getElementById('ne-extract') as HTMLButtonElement | null;
	const pdfInput = document.getElementById('ne-pdf-file') as HTMLInputElement | null;
	const uploadZone = document.getElementById('ne-upload-zone') as HTMLElement | null;
	const fileInfo = document.getElementById('ne-file-info') as HTMLElement | null;
	const pdfExtract = document.getElementById('ne-pdf-extract') as HTMLButtonElement | null;
	const pdfStatus = document.getElementById('ne-pdf-status') as HTMLElement | null;
	const pdfFallback = document.getElementById('ne-pdf-fallback') as HTMLElement | null;
	const previewWrap = document.getElementById('ne-preview-wrap') as HTMLElement | null;
	const previewToggle = document.getElementById('ne-preview-toggle') as HTMLButtonElement | null;
	const rawPreview = document.getElementById('ne-raw-preview') as HTMLElement | null;
	const planCard = document.getElementById('ne-plan-card') as HTMLElement | null;
	const results = document.getElementById('ne-results') as HTMLElement | null;
	const summary = document.getElementById('ne-summary') as HTMLElement | null;
	const cards = document.getElementById('ne-cards') as HTMLElement | null;
	const catalogueInput = document.getElementById('ne-cat-file') as HTMLInputElement | null;
	const catalogueZone = document.getElementById('ne-cat-zone') as HTMLElement | null;
	const catalogueInfo = document.getElementById('ne-cat-info') as HTMLElement | null;
	const catalogueStatus = document.getElementById('ne-cat-status') as HTMLElement | null;
	const catalogueParse = document.getElementById('ne-cat-parse') as HTMLButtonElement | null;
	const catalogueSummary = document.getElementById('ne-cat-summary') as HTMLElement | null;
	const catalogueReview = document.getElementById('ne-cat-review') as HTMLElement | null;
	const matchSection = document.getElementById('ne-match-section') as HTMLElement | null;
	const matchCards = document.getElementById('ne-match-cards') as HTMLElement | null;

	if (
		!input || !extract || !pdfInput || !uploadZone || !fileInfo || !pdfExtract || !pdfStatus || !pdfFallback
		|| !previewWrap || !previewToggle || !rawPreview || !planCard || !results || !summary || !cards
		|| !catalogueInput || !catalogueZone || !catalogueInfo || !catalogueStatus || !catalogueParse
		|| !catalogueSummary || !catalogueReview || !matchSection || !matchCards
	) return;

	let selectedPdf: File | null = null;
	let selectedCatalogue: File | null = null;
	let catalogueItems: CatalogueItem[] = [];
	let catalogueMatches: BudgetCatalogueResult[] = [];
	let catalogueFileName = '';
	let catalogueItemCount = 0;
	let currentModel: NdisPlanModel | null = null;
	const selectedCatalogueIds = new Set<string>();

	const setPdfStatus = (status: PdfExtractionStatus): void => {
		pdfStatus.className = `ne-status ne-status-${status}`;
		pdfStatus.textContent = `Status: ${status}`;
		pdfFallback.hidden = status !== 'error';
	};

	const setCatalogueStatus = (status: 'idle' | 'loading' | 'done' | 'error', message?: string): void => {
		catalogueStatus.className = `ne-status ne-status-${status}`;
		catalogueStatus.textContent = `Status: ${message ?? status}`;
	};

	const updateCatalogueSummary = (): void => {
		if (catalogueItemCount === 0) {
			catalogueSummary.hidden = true;
			catalogueSummary.textContent = '';
			return;
		}

		catalogueSummary.hidden = false;
		catalogueSummary.textContent = `Loaded ${catalogueItemCount} support items from ${catalogueFileName}`;
	};

	const refreshCatalogueReview = (): void => {
		renderCatalogueReviewPanel(catalogueReview, catalogueItems, catalogueFileName, selectedCatalogueIds);
	};

	const refreshCatalogueMatches = (): void => {
		if (!currentModel) {
			renderCatalogueMatches(matchSection, matchCards, null, catalogueMatches, catalogueItems.length > 0);
			return;
		}

		if (catalogueItems.length > 0) {
			catalogueMatches = matchBudgetsToCatalogue(currentModel.budgets, catalogueItems);
		}

		renderCatalogueMatches(matchSection, matchCards, currentModel, catalogueMatches, catalogueItems.length > 0);
	};

	const runExtraction = (text: string): void => {
		const model = extractPlanModel(text);
		currentModel = model;
		renderPlanCard(planCard, model.metadata);
		results.hidden = false;
		renderModelSummary(summary, model);
		renderAllSections(cards, summary, model);
		refreshCatalogueMatches();
	};

	const selectFile = (file: File | null): void => {
		if (!file) return;
		selectedPdf = file;
		fileInfo.hidden = false;
		fileInfo.textContent = `${file.name} (${formatFileSize(file.size)})`;
		pdfExtract.disabled = false;
		setPdfStatus('idle');
	};

	const selectCatalogueFile = (file: File | null): void => {
		if (!file) return;
		selectedCatalogue = file;
		catalogueInfo.hidden = false;
		catalogueInfo.textContent = `${file.name} (${formatFileSize(file.size)})`;
		catalogueParse.disabled = false;
		setCatalogueStatus('idle');
	};

	uploadZone.addEventListener('click', () => {
		pdfInput.click();
	});

	uploadZone.addEventListener('keydown', (event) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			pdfInput.click();
		}
	});

	uploadZone.addEventListener('dragover', (event) => {
		event.preventDefault();
		uploadZone.classList.add('ne-dragging');
	});

	uploadZone.addEventListener('dragleave', () => {
		uploadZone.classList.remove('ne-dragging');
	});

	uploadZone.addEventListener('drop', (event) => {
		event.preventDefault();
		uploadZone.classList.remove('ne-dragging');
		selectFile(event.dataTransfer?.files.item(0) ?? null);
	});

	pdfInput.addEventListener('change', () => {
		selectFile(pdfInput.files?.item(0) ?? null);
	});

	catalogueZone.addEventListener('click', () => {
		catalogueInput.click();
	});

	catalogueZone.addEventListener('keydown', (event) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			catalogueInput.click();
		}
	});

	catalogueZone.addEventListener('dragover', (event) => {
		event.preventDefault();
		catalogueZone.classList.add('ne-dragging');
	});

	catalogueZone.addEventListener('dragleave', () => {
		catalogueZone.classList.remove('ne-dragging');
	});

	catalogueZone.addEventListener('drop', (event) => {
		event.preventDefault();
		catalogueZone.classList.remove('ne-dragging');
		selectCatalogueFile(event.dataTransfer?.files.item(0) ?? null);
	});

	catalogueInput.addEventListener('change', () => {
		selectCatalogueFile(catalogueInput.files?.item(0) ?? null);
	});

	catalogueParse.addEventListener('click', () => {
		if (!selectedCatalogue) return;
		catalogueParse.disabled = true;
		setCatalogueStatus('loading', 'loading');
		void readFileAsArrayBuffer(selectedCatalogue)
			.then((arrayBuffer) => {
				catalogueItems = parseCatalogueXlsx(arrayBuffer);
				localStorage.setItem('hub-ndis-catalogue', JSON.stringify(catalogueItems));
				catalogueFileName = selectedCatalogue?.name ?? '';
				catalogueItemCount = catalogueItems.length;
				selectedCatalogueIds.clear();
				updateCatalogueSummary();
				refreshCatalogueReview();
				refreshCatalogueMatches();
				setCatalogueStatus('done', `loaded ${catalogueItemCount} support items`);
			})
			.catch(() => {
				catalogueItems = [];
				catalogueMatches = [];
				catalogueItemCount = 0;
				localStorage.removeItem('hub-ndis-catalogue');
				selectedCatalogueIds.clear();
				updateCatalogueSummary();
				refreshCatalogueReview();
				refreshCatalogueMatches();
				setCatalogueStatus('error', 'error reading catalogue');
			})
			.finally(() => {
				catalogueParse.disabled = false;
			});
	});

	previewToggle.addEventListener('click', () => {
		const expanded = rawPreview.classList.toggle('ne-expanded');
		previewToggle.setAttribute('aria-expanded', String(expanded));
		previewToggle.textContent = expanded ? 'Hide extracted raw text' : 'Show extracted raw text';
	});

	pdfExtract.addEventListener('click', () => {
		if (!selectedPdf) return;
		pdfExtract.disabled = true;
		setPdfStatus('extracting');
		void extractTextFromPdf(selectedPdf)
			.then((text) => {
				rawPreview.textContent = text || 'No readable text was extracted from this PDF.';
				rawPreview.classList.remove('ne-expanded');
				previewToggle.setAttribute('aria-expanded', 'false');
				previewToggle.textContent = 'Show extracted raw text';
				previewWrap.hidden = false;
				input.value = text;
				runExtraction(text);
				setPdfStatus(text.length > 0 ? 'done' : 'error');
			})
			.catch(() => {
				setPdfStatus('error');
			})
			.finally(() => {
				pdfExtract.disabled = false;
			});
	});

	extract.addEventListener('click', () => {
		runExtraction(input.value);
	});
}

export function isNdisExtractionPath(): boolean {
	return /\/(?:ndis-extraction|ndis\/extraction)\/?$/.test(window.location.pathname);
}
